// Where the page lives, in the top bar: "Tasks" above a task, "Automations
// › Runs" above a run. Built from the URL with the same names as the
// sidebar (features/navigation/navModel); every crumb but the last, all
// links. The last one is the page itself, which its own h1 already names,
// so a top-level page shows nothing here (the empty spacer keeps the bar's
// layout); a hub's own pages show the hub's tabs instead (CustomerHeader).
// Not a heading.

import { Fragment } from "react";
import { ChevronRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import { cx } from "@/components/ui/controlStyles";
import { crumbsFor, type Crumb } from "@/features/navigation/crumbs";
import { useAgentDirectory } from "@/hooks/useAgentDirectory";

interface BreadcrumbsProps {
  customerId: string;
  className?: string;
}

export function Breadcrumbs({ customerId, className }: BreadcrumbsProps) {
  const { pathname } = useLocation();
  const directory = useAgentDirectory(customerId);
  const crumbs = crumbsFor(pathname, directory.nameOf)
    .slice(0, -1)
    .filter((c): c is Crumb & { to: string } => !!c.to);
  if (crumbs.length === 0) return <div className={className} />;

  return (
    <nav aria-label="Breadcrumb" className={cx("min-w-0", className)}>
      <ol className="flex min-w-0 items-center gap-1.5 text-base">
        {crumbs.map((c, i) => (
          <Fragment key={`${i}-${c.label}`}>
            {i > 0 && (
              <li aria-hidden className="flex shrink-0 text-fg-subtle">
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </li>
            )}
            <li className={cx("min-w-0", i === crumbs.length - 1 ? "truncate" : "shrink-0")}>
              <Link
                to={c.to}
                className="rounded-control text-fg-muted transition-colors [@media(hover:hover)]:hover:text-fg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
              >
                {c.label}
              </Link>
            </li>
          </Fragment>
        ))}
      </ol>
    </nav>
  );
}
